import { CalendarOff } from 'lucide-react';

import { Barra } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { ErroDaLista } from '@/componentes/ErroDaLista';

import { useParadasProgramadas } from './consultas';
import { numeroDaOrdem, ROTULO_DO_TIPO } from './ordem';
import { horasDaAgenda, momentoDaParada, ROTULO_DO_MOMENTO, textoDeQuemCombinou } from './parada';

/**
 * AS MÁQUINAS PROGRAMADAS PARA PARAR HOJE (0089; desenho da R06, p.70) — o
 * que a Operação precisa saber da manutenção planejada.
 *
 * O cartão fica ENTRE OS INDICADORES E O FILTRO, em Operação e em
 * Manutenção, e SOME quando não há parada no dia: um cartão dizendo "nenhuma
 * parada" todo dia seria ruído no topo de duas telas. Cada parada é um bloco
 * na tinta violeta da logística — "TRT-003 · 08:00 às 12:00" e, embaixo, o
 * tipo e a O.S. O ícone é `calendar-off` (conceito novo da R06, "Parada
 * programada"); `calendar-clock` fica para prazo e vencimento.
 *
 * Pede `operacao.ler`, e não `os.ler`: quem planeja a frente não precisa ler a
 * O.S. inteira. Sem a permissão, o cartão diz isso — o quinto estado (p.34) —,
 * e não "nenhuma parada", que seria uma afirmação falsa.
 *
 * O "Ver agenda" do desenho não entra: o sistema não tem a tela da agenda, e
 * botão que não leva a lugar nenhum não entra.
 */
export function AgendaDeParadas({ pode }: { readonly pode: (codigo: string) => boolean }) {
  const permitido = pode('operacao.ler');
  const paradas = useParadasProgramadas(permitido);

  if (permitido && paradas.data?.length === 0) return null;

  return (
    <section className="cartao flex flex-col gap-4 p-4" aria-label="Paradas programadas para hoje">
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="flex size-[var(--indicador-bloco)] shrink-0 items-center justify-center rounded-controle bg-deslocamento-fundo">
          <CalendarOff className="size-[var(--indicador-icone)] text-deslocamento" />
        </span>
        <h2 className="txt-subtitulo text-tinta">Paradas programadas para hoje</h2>
      </div>
      {!permitido ? (
        <EstadoVazio
          icone={CalendarOff}
          tom="atencao"
          titulo="A agenda de paradas pede outro perfil"
          descricao="Ela é da Operação: peça ao administrador da empresa o acesso à Operação (planejador de manutenção, oficina ou gestor operacional)."
        />
      ) : paradas.isPending ? (
        <ul aria-hidden="true" className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
          {[0, 1, 2].map((i) => (
            <li key={i} className="flex flex-col gap-2 rounded-controle bg-rebaixada p-3">
              <Barra className="h-4 w-3/4" />
              <Barra className="h-3 w-1/2" />
            </li>
          ))}
        </ul>
      ) : paradas.isError ? (
        <ErroDaLista
          oQue="As paradas programadas"
          erro={paradas.error}
          aoTentarDeNovo={() => {
            void paradas.refetch();
          }}
        />
      ) : (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
          {paradas.data.map((p) => {
            const de = new Date(p.de);
            const ate = new Date(p.ate);
            const momento = momentoDaParada(de, ate);
            const quem = textoDeQuemCombinou(p.combinadaCom, p.combinadaPor);
            return (
              <li
                key={p.ordemId}
                title={[ROTULO_DO_MOMENTO[momento], quem].filter(Boolean).join(' · ')}
                className="flex flex-col gap-1 rounded-controle bg-deslocamento-fundo p-3"
              >
                <span className="txt-botao text-tinta">
                  <span className="codigo">{p.equipamento}</span>
                  {` · ${horasDaAgenda(de, ate)}`}
                </span>
                <span className="txt-apoio text-deslocamento-texto">
                  {[p.servico ?? ROTULO_DO_TIPO[p.tipo], numeroDaOrdem(p.numero)].join(' · ')}
                </span>
                {/* Passou da volta combinada: o atraso é escrito, em vermelho (p.09). */}
                {momento === 'atrasada' ? <span className="txt-legenda text-parada-texto">{ROTULO_DO_MOMENTO[momento]}</span> : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
